import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Award, TrendingUp, Sparkles, Users, ExternalLink, Share2, Trophy } from "lucide-react";
import { formatINR } from "@/lib/api";

const Bar = ({ label, hint, have, haveLabel, req, reqLabel, more, testId }) => {
  const pct = !req ? 100 : Math.min(100, Math.round((have / req) * 100));
  return (
    <div data-testid={testId}>
      <div className="flex items-end justify-between text-xs mb-2">
        <span className="font-bold text-emerald-100/90">{label}{hint && <span className="font-normal text-emerald-100/50"> {hint}</span>}</span>
        <span className="font-mono text-emerald-100/60">
          <span className="font-black text-amber-300 text-sm">{haveLabel}</span> / {reqLabel}
          {more && <span className="ml-2 text-amber-400 font-bold">· {more}</span>}
        </span>
      </div>
      <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-300 shadow-[0_0_12px_rgba(251,191,36,0.5)] transition-[width] duration-700 ease-out" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export const RankCard = ({ rank, levelNames, partnerLabel, landingUrl }) => {
  if (!rank || !levelNames || levelNames.length < 1) return null;
  const currentName = rank.rank_index === 0 ? (partnerLabel || "Financial Consultant") : levelNames[rank.rank_index - 1];
  const isMaxed = rank.rank_index >= levelNames.length;
  const nextName = isMaxed ? null : levelNames[rank.rank_index];
  const crit = isMaxed ? null : (rank.criteria?.[rank.rank_index] || null);
  const qualified = Number(rank.qualified_directs ?? rank.direct_count ?? 0);
  const need = {
    direct: Math.max(0, (crit?.required_direct || 0) - (rank.direct_count || 0)),
    qual: Math.max(0, (crit?.required_direct || 0) - qualified),
    comm: Math.max(0, (crit?.required_commission || 0) - (rank.earned || 0)),
  };
  const ready = need.direct === 0 && need.qual === 0 && need.comm === 0;
  const copyLanding = () => {
    navigator.clipboard.writeText(`👋 I'm a certified financial consultant on Crece. Loans, insurance, claims help and a free AI policy checkup — see my page:\n${landingUrl}`);
    toast.success("Message + link copied — paste anywhere!");
  };

  return (
    <div className="relative overflow-hidden bg-emerald-900 text-white rounded-2xl p-7 sm:p-9" data-testid="rank-card">
      {/* ambient glow layers */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-amber-400/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-emerald-400/10 blur-3xl" />
      <Trophy className="pointer-events-none absolute -right-8 -bottom-10 w-56 h-56 text-white/[0.04]" />

      <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8">
        {/* identity */}
        <div className="md:col-span-5">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-amber-300">
            <Award className="w-4 h-4" /> Current rank
          </div>
          <div className="font-display text-4xl lg:text-5xl font-black tracking-tight mt-3" data-testid="rank-current">{currentName}</div>
          {isMaxed ? (
            <p className="text-sm text-amber-200 mt-4 font-semibold">🎉 You{"'"}ve reached the highest rank. Top of the ladder!</p>
          ) : (
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-100/50">Next rank</div>
                <div className="font-display text-xl font-black text-emerald-50" data-testid="rank-next-name">{nextName}</div>
              </div>
              {ready ? (
                <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 text-amber-300 text-[11px] font-bold uppercase tracking-wider px-4 py-2 rounded-full" data-testid="rank-ready-badge">
                  <span className="relative flex w-2 h-2"><span className="animate-ping absolute inline-flex w-full h-full rounded-full bg-amber-400 opacity-75" /><span className="relative inline-flex rounded-full w-2 h-2 bg-amber-400" /></span>
                  Ready — promotion auto-applies
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 text-amber-300 text-[11px] font-bold uppercase tracking-wider px-4 py-2 rounded-full">
                  <Sparkles className="w-3.5 h-3.5" /> Almost there
                </span>
              )}
            </div>
          )}
        </div>

        {/* progress metrics */}
        {!isMaxed && crit && (
          <div className="md:col-span-7 space-y-5 md:border-l md:border-white/10 md:pl-8" data-testid="rank-progress-bars">
            {crit.required_direct > 0 && (
              <Bar testId="rank-bar-direct" label="Direct recruits"
                have={rank.direct_count || 0} haveLabel={rank.direct_count || 0}
                req={crit.required_direct} reqLabel={crit.required_direct}
                more={need.direct > 0 ? `${need.direct} more` : null} />
            )}
            {crit.required_per_direct > 0 && (
              <Bar testId="rank-bar-qualified" label="Qualified directs" hint={`(each with ${crit.required_per_direct}+ recruits)`}
                have={qualified} haveLabel={qualified}
                req={crit.required_direct} reqLabel={crit.required_direct}
                more={need.qual > 0 ? `${need.qual} more` : null} />
            )}
            {crit.required_commission > 0 && (
              <Bar testId="rank-bar-commission" label="Commission earned"
                have={rank.earned || 0} haveLabel={formatINR(rank.earned || 0)}
                req={crit.required_commission} reqLabel={formatINR(crit.required_commission)}
                more={need.comm > 0 ? `${formatINR(need.comm)} more` : null} />
            )}

            {(need.direct > 0 || need.qual > 0) && (
              <button onClick={copyLanding} data-testid="rank-cta-recruit"
                className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-emerald-950 font-black rounded-full px-6 py-3 text-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-lg hover:shadow-amber-400/25 active:scale-95">
                <Users className="w-4 h-4" />
                {need.direct > 0
                  ? `Recruit ${need.direct} more to unlock ${nextName}`
                  : `${need.qual} more of your directs need ${crit.required_per_direct} sub-recruit${crit.required_per_direct === 1 ? "" : "s"}`}
                <ExternalLink className="w-3.5 h-3.5 opacity-60" />
              </button>
            )}
            {ready === false && need.direct === 0 && need.qual === 0 && need.comm > 0 && (
              <Link to="/app/products" data-testid="rank-cta-earn"
                className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-emerald-950 font-black rounded-full px-6 py-3 text-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-lg hover:shadow-amber-400/25 active:scale-95">
                <Share2 className="w-4 h-4" /> Share products to earn {formatINR(need.comm)} more
              </Link>
            )}
            {ready && (
              <div className="flex items-center gap-2 text-sm font-bold text-amber-200"><TrendingUp className="w-4 h-4" /> Promotion applies automatically — keep going!</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RankCard;
